// Mock factories for flow / output / library codes.
// Each factory returns a fake `data` payload shaped like the API response so
// the corresponding React component can be mounted in the preview modal
// without a real HEC-RAS project being loaded.

const defaultGlobalVariables = {
    plan_title: 'Mock Plan',
    river: 'Mock River',
    reach: 'Mock Reach',
    profile_names: ['10yr', '50yr', '100yr', '500yr'],
};

export const flowOutputMocks = {
    // ---- B27 / OP 03 ---------------------------------------------------------
    'OP 03': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'OP 03',
                    length: 2,
                    data: [
                        {
                            cross_section: 'RS 12345',
                            profiles: [
                                {profile: '10yr', crossing: -0.3},
                                {profile: '100yr', crossing: -1.4},
                            ],
                        },
                        {
                            cross_section: 'RS 9820',
                            profiles: [
                                {profile: '50yr', crossing: -0.6},
                                {profile: '500yr', crossing: -2.1},
                            ],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B28 / OP 04 ---------------------------------------------------------
    'OP 04': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'OP 04',
                    data: [
                        {
                            cross_section: 'RS 12345',
                            profile: '100yr',
                            profiles: [
                                {profile: '10yr', drawdown: -0.4, drawdown_slope: -0.0008},
                                {profile: '100yr', drawdown: -1.2, drawdown_slope: -0.0020},
                            ],
                        },
                        {
                            cross_section: 'RS 9820',
                            profile: '500yr',
                            profiles: [
                                {profile: '500yr', drawdown: -0.9, drawdown_slope: -0.0017},
                            ],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B29 / FL 05 ---------------------------------------------------------
    'FL 05': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'FL 05',
                    data: [
                        {
                            type: 'Bridge',
                            cross_section: 'RS 12345',
                            profiles: ['10yr', '100yr'],
                            culvert_name: 'BR-01',
                            culvert_type: 'Bridge',
                            us: {cross_section: 'RS 12360', data: [1250, 1825]},
                            ds: {cross_section: 'RS 12330', data: [1240, 1815]},
                        },
                        {
                            type: 'Culvert',
                            cross_section: 'RS 8100',
                            profiles: ['50yr', '500yr'],
                            culvert_name: 'CV-02',
                            culvert_type: 'Culvert',
                            us: {cross_section: 'RS 8115', data: [430, 760]},
                            ds: {cross_section: 'RS 8085', data: [425, 755]},
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B30 / ST 06 ---------------------------------------------------------
    'ST 06': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'ST 06',
                    data: [
                        {
                            cross_section: 'RS 12345',
                            type: 'Bridge',
                            profiles: ['10yr', '100yr'],
                        },
                        {
                            cross_section: 'RS 9820',
                            type: 'Culvert',
                            profiles: ['50yr', '500yr'],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B31 / XY 12 ---------------------------------------------------------
    'XY 12': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XY 12',
                    data: [
                        {cross_section: 'RS 12345', schematic: '12350.10', difference: 5.1},
                        {cross_section: 'RS 9820', schematic: '9817.20', difference: 2.8},
                    ],
                },
            ],
        },
    }),

    // ---- B31 / LTSTR 01 (no explicit case; mock anyway) ---------------------
    'LTSTR 01': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'LTSTR 01',
                    data: [
                        {cross_section: 'RS 12345'},
                        {cross_section: 'RS 9820'},
                    ],
                },
            ],
        },
    }),

    // ---- B31 / XY 18 ---------------------------------------------------------
    'XY 18': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XY 18',
                    data: [
                        {cross_section: 'RS 12345'},
                        {cross_section: 'RS 9820'},
                        {cross_section: 'RS 5200'},
                    ],
                },
            ],
        },
    }),

    // ---- B38 / FL 06 ---------------------------------------------------------
    'FL 06': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'FL 06',
                    data: [
                        {cross_section: 'RS 12345'},
                        {cross_section: 'RS 9820'},
                    ],
                },
            ],
        },
    }),

    // ---- B40 / INF 01 (Bridge + Culvert info tables) ------------------------
    'INF 01': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'INF 01',
                    data: [
                        {
                            type: 'Bridge',
                            cross_section: 'RS 12345',
                            up_thalweg: [1250.0, 825.5],
                            down_thalweg: [1248.5, 824.8],
                            up_bank_left: 1120.0,
                            up_bank_right: 1380.0,
                            down_bank_left: 1118.5,
                            down_bank_right: 1378.5,
                            up_bank_left_elev: 832.0,
                            up_bank_right_elev: 833.2,
                            down_bank_left_elev: 831.5,
                            down_bank_right_elev: 832.8,
                            up_left_xs_station: 1100.0,
                            up_right_xs_station: 1400.0,
                            down_left_xs_station: 1099.0,
                            down_right_xs_station: 1399.0,
                            up_left_xs_footnote: '1',
                            up_right_xs_footnote: '2',
                            down_left_xs_footnote: '1',
                            down_right_xs_footnote: '2',
                            up_left_xs_elevation: 835.0,
                            up_right_xs_elevation: 835.5,
                            down_left_xs_elevation: 834.5,
                            down_right_xs_elevation: 835.0,
                            up_left_opening_station: 1150.0,
                            up_right_opening_station: 1350.0,
                            down_left_opening_station: 1149.0,
                            down_right_opening_station: 1349.0,
                            dist_to_xs: 15.0,
                            down_dist: 14.5,
                            bridge_us_width: 30.0,
                            up_dist_btw_xs: 45.0,
                            up_left_diff: 12.5,
                            up_right_diff: 13.2,
                            down_left_diff: 12.8,
                            down_right_diff: 13.4,
                            up_left_low_chord_elev: 845.0,
                            up_right_low_chord_elev: 845.3,
                            down_left_low_chord_elev: 844.8,
                            down_right_low_chord_elev: 845.1,
                            ulr_high_elev: 850.0,
                            urr_high_elev: 850.5,
                            dlr_high_elev: 849.8,
                            drr_high_elev: 850.3,
                            ulr_high_sta: 1150.0,
                            urr_high_sta: 1350.0,
                            dlr_high_sta: 1149.0,
                            drr_high_sta: 1349.0,
                            ulr_top_rail: 852.0,
                            urr_top_rail: 852.5,
                            dlr_top_rail: 851.8,
                            drr_top_rail: 852.3,
                            ulr_rail_height: 2.0,
                            urr_rail_height: 2.0,
                            dlr_rail_height: 2.0,
                            drr_rail_height: 2.0,
                            up_left_ratio: 0.5,
                            up_right_ratio: 0.5,
                            down_left_ratio: 0.3,
                            down_right_ratio: 0.3,
                            vc_us_left: 10.5,
                            vc_us_right: 10.8,
                            vc_ds_left: 10.6,
                            vc_ds_right: 10.9,
                            auto_distance_us_min: 10.0,
                            auto_distance_us_max: 30.0,
                            auto_distance_ds_min: 10.0,
                            auto_distance_ds_max: 30.0,
                            skew: 5,
                            skew_map: 5,
                        },
                        {
                            type: 'Culvert',
                            cross_section: 'RS 8100',
                            us_bank_left: 420.0,
                            us_bank_right: 480.0,
                            ds_bank_left: 419.0,
                            ds_bank_right: 479.0,
                            us_bank_left_elev: 220.0,
                            us_bank_right_elev: 220.5,
                            ds_bank_left_elev: 219.5,
                            ds_bank_right_elev: 220.0,
                            up_left_xs_station: 415.0,
                            up_right_xs_station: 485.0,
                            down_left_xs_station: 414.0,
                            down_right_xs_station: 484.0,
                            up_left_xs_footnote: '1',
                            up_right_xs_footnote: '',
                            down_left_xs_footnote: '',
                            down_right_xs_footnote: '2',
                            up_left_xs_station_elev: 222.0,
                            up_right_xs_station_elev: 222.5,
                            down_left_xs_station_elev: 221.5,
                            down_right_xs_station_elev: 222.0,
                            us_left_opening_sta: 440.0,
                            us_right_opening_sta: 460.0,
                            ds_left_opening_sta: 439.0,
                            ds_right_opening_sta: 459.0,
                            us_dist_btw_xs: 20.0,
                            up_left_diff: 5.0,
                            up_right_diff: 5.2,
                            down_left_diff: 5.1,
                            down_right_diff: 5.3,
                            up_left_ratio: 0.4,
                            up_right_ratio: 0.4,
                            down_left_ratio: 0.3,
                            down_right_ratio: 0.3,
                            us_low_high_elev: 228.0,
                            ds_low_high_elev: 227.8,
                            us_low_high_sta: 440.0,
                            ds_low_high_sta: 439.0,
                            vc_us_left: 7.5,
                            vc_us_right: 7.6,
                            vc_ds_left: 7.4,
                            vc_ds_right: 7.5,
                            auto_distance_us_min: 5.0,
                            auto_distance_us_max: 15.0,
                            auto_distance_ds_min: 5.0,
                            auto_distance_ds_max: 15.0,
                            culverts: [
                                {
                                    name: 'Culvert #1',
                                    us_dist_to_xs: 5.0,
                                    ds_dist_to_xs: 5.0,
                                    culvert_length: 40.0,
                                    us_invert_elev: 218.0,
                                    ds_invert_elev: 217.5,
                                    barrels: [
                                        {name: 'Barrel #1', us: 450.0, ds: 449.0},
                                        {name: 'Barrel #2', us: 455.0, ds: 454.0},
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B40 / INF 02 (Bridge/Culvert info table) ---------------------------
    'INF 02': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'INF 02',
                    data: [
                        {
                            type: 'Bridge',
                            cross_section: 'RS 12345',
                            description: 'Concrete deck girder bridge over Mock River',
                            bridge_us_width: 30.0,
                            span_widths_us: [25.0, 25.0],
                            span_widths_ds: [25.0, 25.0],
                            pier_shape_name: 'Round Nose',
                            piers_us: [{chord_count: 2}, {chord_count: 2}],
                            abutment_slopes_us: {left: 2.0, right: 2.0},
                            abutment_slopes_ds: {left: 1.5, right: 1.5},
                        },
                        {
                            type: 'Culvert',
                            cross_section: 'RS 8100',
                            description: 'Concrete box culvert',
                            culverts: [
                                {
                                    name: 'Culvert #1',
                                    shape: 'Box',
                                    culvert_length: 40.0,
                                    span: 6.0,
                                    rise: 4.0,
                                    chart_name: '8',
                                    scale_name: '1',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B40 / ST 12 (abutment slopes table) --------------------------------
    'ST 12': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'ST 12',
                    data: [
                        {
                            cross_section: 'RS 12345',
                            abutment_left: {
                                us: [
                                    {station: 1100.0, elev: 832.0, slope: 2.0, mark_red: false},
                                    {station: 1110.0, elev: 836.0, slope: 1.5, mark_red: false},
                                ],
                                ds: [
                                    {station: 1099.0, elev: 831.5, slope: 1.8, mark_red: false},
                                ],
                            },
                            abutment_right: {
                                us: [
                                    {station: 1400.0, elev: 833.0, slope: 2.5, mark_red: true},
                                    {station: 1390.0, elev: 837.0, slope: 1.4, mark_red: false},
                                ],
                                ds: [
                                    {station: 1399.0, elev: 832.5, slope: 1.7, mark_red: false},
                                ],
                            },
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B40 / ST 13 (culvert invert slope, US lower) -----------------------
    'ST 13': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'ST 13',
                    data: [
                        {
                            cross_section: 'RS 8100',
                            culvert_name: 'Culvert #1',
                            us_invert: 217.5,
                            ds_invert: 218.0,
                            slope: -0.0125,
                        },
                        {
                            cross_section: 'RS 5200',
                            culvert_name: 'Culvert #2',
                            us_invert: 120.2,
                            ds_invert: 120.5,
                            slope: -0.0075,
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B40 / ST 14 (culvert slope > threshold) ----------------------------
    'ST 14': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'ST 14',
                    data: [
                        {
                            cross_section: 'RS 8100',
                            culvert_name: 'Culvert #1',
                            us_invert: 218.0,
                            ds_invert: 217.0,
                            slope: 0.025,
                            threshold: 0.02,
                        },
                        {
                            cross_section: 'RS 5200',
                            culvert_name: 'Culvert #2',
                            us_invert: 120.5,
                            ds_invert: 119.4,
                            slope: 0.0275,
                            threshold: 0.02,
                        },
                    ],
                },
            ],
        },
    }),

    // ---- B40 / ST 15 (bridge opening width inconsistent) --------------------
    'ST 15': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'ST 15',
                    data: [
                        {cross_section: 'RS 12345', us_bow: 28.5, ds_bow: 30.5},
                        {cross_section: 'RS 9820', us_bow: 24.0, ds_bow: 27.5},
                    ],
                },
            ],
        },
    }),

    // ---- FL1 / FL 03 (flow change rate) -------------------------------------
    'FL 03': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'FL 03',
                    profiles: ['10yr', '50yr', '100yr', '500yr'],
                    data: [
                        {
                            cross_section: 'RS 12345',
                            flow_changes: [
                                {profile: '10yr', flow_change: 5.0},
                                {profile: '50yr', flow_change: 12.5},
                                {profile: '100yr', flow_change: 25.0},
                                {profile: '500yr', flow_change: 32.0},
                            ],
                        },
                        {
                            cross_section: 'RS 9820',
                            flow_changes: [
                                {profile: '10yr', flow_change: 3.0},
                                {profile: '50yr', flow_change: 8.0},
                                {profile: '100yr', flow_change: 15.0},
                                {profile: '500yr', flow_change: 21.0},
                            ],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- FL5 / FL 07 (normal depth slope) -----------------------------------
    'FL 07': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'FL 07',
                    ds_chn: 250.0,
                    ds_chn_slope: 0.0035,
                    data: [
                        {
                            cross_section: 'RS 250',
                            normal_depths: [
                                {profile: '10yr', name: 'Normal Depth S', downstream: 0.005, eg_slope: 0.0048},
                                {profile: '50yr', name: 'Normal Depth S', downstream: 0.012, eg_slope: 0.0115},
                                {profile: '100yr', name: 'Normal Depth S', downstream: 0.020, eg_slope: 0.0190},
                                {profile: '500yr', name: 'Known WS', downstream: 825.5, eg_slope: 0.0210},
                            ],
                        },
                    ],
                },
            ],
        },
    }),

    // ---- OP5 / OP 05 (WSEL defaults to critical depth) ----------------------
    'OP 05': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'OP 05',
                    data: [
                        {cross_section: 'RS 12345', profiles: ['10yr', '50yr'], slope: 0.0125},
                        {cross_section: 'RS 9820', profiles: ['100yr', '500yr'], slope: 0.0182},
                    ],
                },
            ],
        },
    }),

    // ---- XS4 / XS 06 (bank station elev/slope) ------------------------------
    'XS 06': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XS 06',
                    average: 0.045,
                    std: 0.012,
                    data: [
                        {
                            cross_section: 'RS 12345',
                            left_sta_elev: [120.5, 832.0],
                            right_sta_elev: [380.0, 833.5],
                            elev_diff: 1.5,
                            chn_diff: 259.5,
                            slope: 0.058,
                        },
                        {
                            cross_section: 'RS 9820',
                            left_sta_elev: [115.0, 821.0],
                            right_sta_elev: [375.0, 822.8],
                            elev_diff: 1.8,
                            chn_diff: 260.0,
                            slope: 0.069,
                        },
                    ],
                },
            ],
        },
    }),

    // ---- XS4 / XS 08 (channel width deviation) ------------------------------
    'XS 08': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XS 08',
                    median: 180.0,
                    data: [
                        {
                            cross_section: 'RS 12345',
                            top_w_channel: 260.0,
                            dev_reach_avg: 44.4,
                            dev_usds_avg: 38.5,
                        },
                        {
                            cross_section: 'RS 9820',
                            top_w_channel: 130.0,
                            dev_reach_avg: -27.8,
                            dev_usds_avg: -22.1,
                        },
                    ],
                },
            ],
        },
    }),

    // ---- XS4 / XS 10 (channel contains > threshold profile) -----------------
    'XS 10': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XS 10',
                    threshold: '10yr',
                    data: [
                        {cross_section: 'RS 12345', profile: '50yr'},
                        {cross_section: 'RS 9820', profile: '100yr'},
                    ],
                },
            ],
        },
    }),

    // ---- XS5 / XS 07 (thalweg slope too low) --------------------------------
    'XS 07': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XS 07',
                    data: [
                        {cross_section: 'RS 12345', dsrl: 250.0, elev_diff: 0.5, slope: 0.0002},
                        {cross_section: 'RS 9820', dsrl: 230.0, elev_diff: 0.3, slope: 0.0001},
                    ],
                },
            ],
        },
    }),

    // ---- XS5 / XS 09 (thalweg slope too high) -------------------------------
    'XS 09': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XS 09',
                    slope_max_threshold: 0.05,
                    thalweg_times: 3,
                    data: [
                        {cross_section: 'RS 12345', dsrl: 250.0, elev_diff: '4.50', slope: 0.018},
                        {cross_section: 'RS 9820', dsrl: 230.0, elev_diff: '3.20', slope: 0.0139},
                    ],
                },
            ],
        },
    }),

    // ---- XY8 / XY 15 (overbank DSRL diff) -----------------------------------
    'XY 15': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XY 15',
                    data: [
                        {
                            cross_section: 'RS 12345',
                            lob: 250.0,
                            left_schematic: 240.0,
                            left_percent: 4,
                            rob: 250.0,
                            right_schematic: 0,
                            right_percent: 'N/A',
                        },
                        {
                            cross_section: 'RS 9820',
                            lob: 230.0,
                            left_schematic: 220.0,
                            left_percent: 4,
                            rob: 230.0,
                            right_schematic: 225.0,
                            right_percent: 2,
                        },
                    ],
                },
            ],
        },
    }),

    // ---- XY9 / XY 19 (deflection angle) -------------------------------------
    'XY 19': (messageKey) => ({
        message_key: messageKey,
        data: {
            global_variables: defaultGlobalVariables,
            result: [
                {
                    error_id: 'XY 19',
                    data: [
                        {cross_section: 'RS 12345', deflection_angle: 32.5, threshold: 20, deflection_station: 415.2, in_water_extent: true},
                        {cross_section: 'RS 9820', deflection_angle: 28.0, threshold: 20, deflection_station: 88.7, in_water_extent: false},
                    ],
                },
            ],
        },
    }),
};
